import { Box, Typography, styled } from "@mui/material";

import { StyledIndividualTabV2, StyledTabContainer } from "./CustomTab";

// Page title plus its module-level tab strip, with the rule that runs from the
// tabs to the right edge of the page.
//
// Extracted because every page that has this layout was rebuilding it: Smart
// Alerts composes AlertSystemTabs (PagesContent/BmsFaults) with the
// borderBottom declared inline on the page, so the rule is a property of the
// caller rather than of the tabs, and a new page gets the tabs without it --
// which is exactly how Asset Management ended up with a bare strip.
//
// Tabs come from StyledIndividualTabV2, the same primitive Smart Alerts uses,
// so the two pages match by construction rather than by two sets of numbers
// agreeing. Anything visual that needs changing belongs in CustomTab.js.
//
// Existing callers are deliberately NOT migrated here -- that is a separate,
// wider change. New pages should use this.

const PageTab = styled(StyledIndividualTabV2)(() => ({
    fontSize: "15px",
    lineHeight: "24px",
}));

const PageHeaderTabs = ({
    title,
    tabs = [],
    selectedTab,
    onChange = () => {},
    rightContent = null,
    "aria-label": ariaLabel = "page tabs",
}) => {
    // MUI renders no selection at all when `value` matches no Tab, so a slug
    // that is not in the list falls back to the first rather than leaving the
    // strip looking unselected.
    const value = tabs.some((t) => t.slug === selectedTab)
        ? selectedTab
        : tabs[0]?.slug;

    // TITLE ON ITS OWN LINE, tabs beneath it -- the arrangement Inventory uses
    // (InventoryMain.jsx, the reference this repo's CLAUDE.md points at for
    // header spacing). Side by side, the title read as a label for the first
    // tab rather than as the name of the page, and a long module name squeezed
    // the strip.
    //
    // The gap belongs to the title block, not to the tabs: one number, in one
    // place, per the same rule.
    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ px: 3, display: "flex", flexShrink: 0, mb: 2 }}>
                <Typography variant="fw700s20lh24c2E2E2E">{title}</Typography>
            </Box>

            {/* The rule belongs to this row, not to the tabs: it has to run past
                the last tab to the edge of the page, which a Tabs element sized
                to its content cannot do. */}
            <Box
                sx={{
                    display: "flex",
                    minWidth: 0,
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #E4E3E3",
                    pl: 3,
                    pr: 3,
                }}
            >
                <StyledTabContainer
                    value={value}
                    onChange={(_, slug) => onChange(slug)}
                    aria-label={ariaLabel}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                >
                    {tabs.map((tab) => (
                        <PageTab
                            key={tab.slug}
                            value={tab.slug}
                            label={tab.name}
                            data-testid={`page-tab-${tab.slug}`}
                        />
                    ))}
                </StyledTabContainer>

                {rightContent}
            </Box>
        </Box>
    );
};

export default PageHeaderTabs;
